'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

type ModalType = 'shock-parameters' | 'borrowing-instruments';

interface ModalState {
  isShockModalOpen: boolean;
  isBorrowingModalOpen: boolean;
}

interface ModalContextValue {
  modalState: ModalState;
  openModal: (type: ModalType) => void;
  closeModal: (type: ModalType) => void;
  setModalState: (type: ModalType, isOpen: boolean) => void;
  subscribe: (callback: (state: ModalState) => void) => () => void;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export function ModalStateProvider({ children }: { children: ReactNode }) {
  const [modalState, setModalStateInternal] = useState<ModalState>({
    isShockModalOpen: false,
    isBorrowingModalOpen: false,
  });

  const [subscribers, setSubscribers] = useState<Set<(state: ModalState) => void>>(new Set());

  const setModalState = useCallback((type: ModalType, isOpen: boolean) => {
    setModalStateInternal((prev) => {
      const newState = {
        ...prev,
        isShockModalOpen: type === 'shock-parameters' ? isOpen : prev.isShockModalOpen,
        isBorrowingModalOpen: type === 'borrowing-instruments' ? isOpen : prev.isBorrowingModalOpen,
      };
      
      // Notify all subscribers
      subscribers.forEach((callback) => {
        try {
          callback(newState);
        } catch (error) {
          console.error('Error in modal state subscriber:', error);
        }
      });
      
      return newState;
    });
  }, [subscribers]);

  const openModal = useCallback((type: ModalType) => {
    setModalState(type, true);
  }, [setModalState]);

  const closeModal = useCallback((type: ModalType) => {
    setModalState(type, false);
  }, [setModalState]);

  const subscribe = useCallback((callback: (state: ModalState) => void) => {
    setSubscribers((prev) => new Set(prev).add(callback));
    
    // Return unsubscribe function
    return () => {
      setSubscribers((prev) => {
        const next = new Set(prev);
        next.delete(callback);
        return next;
      });
    };
  }, []);

  const value: ModalContextValue = {
    modalState,
    openModal,
    closeModal,
    setModalState,
    subscribe,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModalState() {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModalState must be used within a ModalStateProvider');
  }
  return context;
}

